<template>
  <div class="customer-show-view">
    <!-- Header Section -->
    <div class="header-section d-flex justify-content-between align-items-start mb-4">
      <div class="header-left">
        <div class="d-flex align-items-center mb-3">
          <button
              class="btn btn-outline-secondary me-3"
              @click="goBack"
              title="Zurück"
          >
            <i class="fas fa-arrow-left"></i>
          </button>
          <h2 class="mb-0">
            <i class="fas fa-user me-2"></i>Kundendetails
          </h2>
        </div>
      </div>

      <div class="header-right" v-if="customer">
        <template v-if="!editing">
          <button class="btn btn-primary me-2" @click="startEdit" title="Kunde bearbeiten">
            <i class="fas fa-edit me-1"></i>Bearbeiten
          </button>
          <button
              class="btn btn-outline-danger"
              @click="deleteCustomer"
              title="Kunde löschen"
          >
            <i class="fas fa-trash me-1"></i>Löschen
          </button>
        </template>
        <template v-else>
          <button class="btn btn-success me-2" @click="saveCustomer" :disabled="savingCustomer">
            <i class="fas fa-save me-1"></i>Speichern
          </button>
          <button class="btn btn-outline-secondary" @click="cancelEdit" :disabled="savingCustomer">
            <i class="fas fa-times me-1"></i>Abbrechen
          </button>
        </template>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Lädt...</span>
      </div>
    </div>

    <!-- Customer Details -->
    <div v-else-if="customer" class="customer-details">
      <!-- Basic Information Card -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="fas fa-info-circle me-2"></i>Grundinformationen
          </h5>
        </div>
        <!-- Read-only view -->
        <div class="card-body" v-if="!editing">
          <div class="row">
            <div class="col-md-6">
              <div class="info-group">
                <label class="info-label">Firmenname:</label>
                <div class="info-value">{{ customer.name || '-' }}</div>
              </div>
              <div class="info-group">
                <label class="info-label">Kundennummer:</label>
                <div class="info-value">{{ customer.customer_number || '-' }}</div>
              </div>
              <div class="info-group">
                <label class="info-label">Telefon:</label>
                <div class="info-value">
                  <a v-if="customer.phone" :href="`tel:${customer.phone}`" class="contact-link">
                    {{ customer.phone }}
                  </a>
                  <span v-else>-</span>
                </div>
              </div>
              <div class="info-group">
                <label class="info-label">Fax:</label>
                <div class="info-value">{{ customer.fax || '-' }}</div>
              </div>
              <div class="info-group">
                <label class="info-label">E-Mail:</label>
                <div class="info-value">
                  <a v-if="customer.email" :href="`mailto:${customer.email}`" class="contact-link">
                    {{ customer.email }}
                  </a>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="info-group">
                <label class="info-label">Straße:</label>
                <div class="info-value">{{ customer.address || '-' }}</div>
              </div>
              <div class="info-group">
                <label class="info-label">PLZ:</label>
                <div class="info-value">{{ customer.postal_code || '-' }}</div>
              </div>
              <div class="info-group">
                <label class="info-label">Ort:</label>
                <div class="info-value">{{ customer.city || '-' }}</div>
              </div>
              <div class="info-group">
                <label class="info-label">Land:</label>
                <div class="info-value">{{ customer.country || '-' }}</div>
              </div>
              <div class="info-group">
                <label class="info-label">Status:</label>
                <div class="info-value">
                  <span class="badge" :class="getCustomerStatusClass(customer.status)">
                    {{ getCustomerStatusText(customer.status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit form -->
        <div class="card-body" v-else>
          <div class="row">
            <div class="col-md-6">
              <div class="info-group">
                <label class="info-label">Firmenname:</label>
                <input v-model="editForm.name" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">Namenszusatz:</label>
                <input v-model="editForm.name_suffix" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">Telefon:</label>
                <input v-model="editForm.phone" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">Fax:</label>
                <input v-model="editForm.fax" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">E-Mail:</label>
                <input v-model="editForm.email" type="email" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">Webseite:</label>
                <input v-model="editForm.website" class="form-control form-control-sm" />
              </div>
            </div>
            <div class="col-md-6">
              <div class="info-group">
                <label class="info-label">Straße:</label>
                <input v-model="editForm.address" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">PLZ:</label>
                <input v-model="editForm.postal_code" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">Ort:</label>
                <input v-model="editForm.city" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">Land:</label>
                <input v-model="editForm.country" class="form-control form-control-sm" />
              </div>
              <div class="info-group">
                <label class="info-label">Branche:</label>
                <input v-model="editForm.industry" class="form-control form-control-sm" />
              </div>
            </div>
            <div class="col-12">
              <div class="info-group">
                <label class="info-label">Notizen:</label>
                <textarea v-model="editForm.notes" rows="3" class="form-control form-control-sm"></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Notes Card -->
      <div class="card mb-4" v-if="customer.notes">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="fas fa-sticky-note me-2"></i>Notizen
          </h5>
        </div>
        <div class="card-body">
          <div class="notes-content">{{ customer.notes }}</div>
        </div>
      </div>

      <!-- Contacts Card -->
      <div class="card mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="card-title mb-0">
            <i class="fas fa-address-book me-2"></i>Ansprechpartner
          </h5>
          <button
              v-if="!showContactForm"
              class="btn btn-sm btn-primary"
              @click="openContactForm"
          >
            <i class="fas fa-plus me-1"></i>Ansprechpartner hinzufügen
          </button>
        </div>
        <div class="card-body">
          <!-- Add-contact form -->
          <div v-if="showContactForm" class="contact-form mb-4">
            <div class="row g-2">
              <div class="col-md-3">
                <label class="info-label">Anrede</label>
                <input v-model="contactForm.salutation" class="form-control form-control-sm" placeholder="Herr / Frau" />
              </div>
              <div class="col-md-4">
                <label class="info-label">Vorname</label>
                <input v-model="contactForm.first_name" class="form-control form-control-sm" />
              </div>
              <div class="col-md-5">
                <label class="info-label">Nachname *</label>
                <input v-model="contactForm.last_name" class="form-control form-control-sm" />
              </div>
              <div class="col-md-6">
                <label class="info-label">E-Mail</label>
                <input v-model="contactForm.email" type="email" class="form-control form-control-sm" />
              </div>
              <div class="col-md-3">
                <label class="info-label">Telefon</label>
                <input v-model="contactForm.phone" class="form-control form-control-sm" />
              </div>
              <div class="col-md-3">
                <label class="info-label">Mobil</label>
                <input v-model="contactForm.mobile" class="form-control form-control-sm" />
              </div>
              <div class="col-md-6">
                <label class="info-label">Abteilung</label>
                <input v-model="contactForm.department" class="form-control form-control-sm" />
              </div>
            </div>
            <div class="mt-3">
              <button class="btn btn-sm btn-success me-2" @click="saveContact" :disabled="savingContact || !contactForm.last_name">
                <i class="fas fa-save me-1"></i>Speichern
              </button>
              <button class="btn btn-sm btn-outline-secondary" @click="cancelContactForm" :disabled="savingContact">
                <i class="fas fa-times me-1"></i>Abbrechen
              </button>
            </div>
          </div>

          <div v-if="contacts && contacts.length > 0" class="row">
            <div v-for="contact in contacts" :key="contact.id" class="col-md-6 mb-3">
              <div class="contact-item">
                <h6 class="contact-name">{{ contact.first_name }} {{ contact.last_name }}</h6>
                <div class="contact-details">
                  <div v-if="contact.email" class="contact-detail">
                    <i class="fas fa-envelope me-2"></i>
                    <a :href="`mailto:${contact.email}`" class="contact-link">{{ contact.email }}</a>
                  </div>
                  <div v-if="contact.phone" class="contact-detail">
                    <i class="fas fa-phone me-2"></i>
                    <a :href="`tel:${contact.phone}`" class="contact-link">{{ contact.phone }}</a>
                  </div>
                  <div v-if="contact.mobile" class="contact-detail">
                    <i class="fas fa-mobile-alt me-2"></i>
                    <a :href="`tel:${contact.mobile}`" class="contact-link">{{ contact.mobile }}</a>
                  </div>
                  <div v-if="contact.department" class="contact-detail">
                    <i class="fas fa-briefcase me-2"></i>
                    {{ contact.department }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p v-else-if="!showContactForm" class="text-muted mb-0">Keine Ansprechpartner vorhanden</p>
        </div>
      </div>

      <!-- Aufträge -->
      <div class="card mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="card-title mb-0">
            <i class="fas fa-clipboard-list me-2"></i>Aufträge
          </h5>
          <span class="orders-count">{{ ordersTotalCount }} {{ ordersTotalCount === 1 ? 'Auftrag' : 'Aufträge' }}</span>
        </div>
        <div class="card-body p-0">
          <DataTable
              v-if="orders.length > 0"
              :columns="orderColumns"
              :data="sortedOrders"
              :loading="false"
              :current-page="ordersPage"
              :total-pages="ordersTotalPages"
              :total-count="ordersTotalCount"
              :compact="true"
              :row-class="getOrderRowClass"
              @sort="handleOrderSort"
              @page-change="handleOrderPageChange"
              @per-page-change="handleOrderPerPageChange"
          >
            <template #order_number="{ item }">
              <router-link :to="`/auftraege/${item.id}`" class="order-link">
                <strong>{{ item.order_number }}</strong>
              </router-link>
            </template>

            <template #employee_name="{ item }">
              <span>{{ item.employee_name || '-' }}</span>
            </template>

            <template #created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>
          </DataTable>

          <div v-else class="empty-state">
            <i class="fas fa-clipboard fa-3x"></i>
            <p>Keine Aufträge vorhanden</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="!loading" class="error-state">
      <div class="alert alert-danger">
        <i class="fas fa-exclamation-triangle me-2"></i>
        Kunde nicht gefunden oder Fehler beim Laden der Daten.
      </div>
    </div>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue'
import { CustomerService, CustomerFormatter } from '@/services/customerService.js'
import { getOrderRowClass, sortOrders } from '@/composables/useOrderStatus'

export default {
  name: 'CustomerShowView',

  components: { DataTable },

  data() {
    return {
      customer: null,
      contacts: [],
      loading: true,
      customerService: null,
      editing: false,
      editForm: {},
      savingCustomer: false,
      showContactForm: false,
      savingContact: false,
      contactForm: {},
      orders: [],
      ordersPage: 1,
      ordersTotalPages: 1,
      ordersTotalCount: 0,
      ordersPerPage: 25,
      orderSortField: 'created_at',
      orderSortDirection: 'desc',
      orderColumns: [
        { key: 'order_number',  label: 'Auftragsnr.',  sortable: true },
        { key: 'description',   label: 'Beschreibung', sortable: true },
        { key: 'employee_name', label: 'Bearbeiter',   sortable: true },
        { key: 'created_at',   label: 'Erstellt',      sortable: true },
      ]
    }
  },

  computed: {
    sortedOrders() {
      return sortOrders(this.orders, this.orderSortField, this.orderSortDirection)
    }
  },

  async mounted() {
    this.customerService = new CustomerService(this);
    await this.loadCustomer();
  },

  methods: {
    async loadCustomer() {
      this.loading = true;
      try {
        const customerId = this.$route.params.id;
        const result = await this.customerService.loadCustomerDetails(customerId, {
          page: this.ordersPage,
          perPage: this.ordersPerPage
        });
        this.customer = result.customer;
        this.contacts = result.contacts || [];
        this.orders = result.orders || [];
        if (result.meta) {
          this.ordersPage = result.meta.current_page || 1;
          this.ordersTotalPages = result.meta.total_pages || 1;
          this.ordersTotalCount = result.meta.total_count || 0;
        }
      } catch (error) {
        console.error('Error loading customer:', error);
        this.$toast?.error('Fehler beim Laden der Kundendaten');
      } finally {
        this.loading = false;
      }
    },

    getOrderRowClass,

    handleOrderSort({ field, direction }) {
      this.orderSortField = field
      this.orderSortDirection = direction
    },

    async handleOrderPageChange(page) {
      this.ordersPage = page
      await this.loadCustomer()
    },

    async handleOrderPerPageChange(perPage) {
      this.ordersPerPage = perPage
      this.ordersPage = 1
      await this.loadCustomer()
    },

    goBack() {
      this.$router.go(-1);
    },

    startEdit() {
      this.editForm = {
        name: this.customer.name,
        name_suffix: this.customer.name_suffix,
        industry: this.customer.industry,
        address: this.customer.address,
        postal_code: this.customer.postal_code,
        city: this.customer.city,
        country: this.customer.country,
        phone: this.customer.phone,
        fax: this.customer.fax,
        email: this.customer.email,
        website: this.customer.website,
        notes: this.customer.notes
      };
      this.editing = true;
    },

    cancelEdit() {
      this.editing = false;
      this.editForm = {};
    },

    async saveCustomer() {
      this.savingCustomer = true;
      try {
        const updated = await this.customerService.updateCustomer(this.customer.id, this.editForm);
        this.customer = { ...this.customer, ...updated };
        this.editing = false;
        this.editForm = {};
        this.$toast?.success('Kunde gespeichert');
      } catch (error) {
        console.error('Error saving customer:', error);
        this.$toast?.error(error.message || 'Fehler beim Speichern');
      } finally {
        this.savingCustomer = false;
      }
    },

    openContactForm() {
      this.contactForm = {
        salutation: '', first_name: '', last_name: '',
        email: '', phone: '', mobile: '', department: ''
      };
      this.showContactForm = true;
    },

    cancelContactForm() {
      this.showContactForm = false;
      this.contactForm = {};
    },

    async saveContact() {
      if (!this.contactForm.last_name) return;
      this.savingContact = true;
      try {
        const contact = await this.customerService.createContact(this.customer.id, this.contactForm);
        this.contacts.push(contact);
        this.showContactForm = false;
        this.contactForm = {};
        this.$toast?.success('Ansprechpartner hinzugefügt');
      } catch (error) {
        console.error('Error creating contact:', error);
        this.$toast?.error(error.message || 'Fehler beim Hinzufügen');
      } finally {
        this.savingContact = false;
      }
    },

    async deleteCustomer() {
      if (confirm('Sind Sie sicher, dass Sie diesen Kunden löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
        try {
          await this.customerService.deleteCustomer(this.customer.id);
          this.$toast?.success('Kunde erfolgreich gelöscht');
          this.$router.push('/kunden');
        } catch (error) {
          console.error('Error deleting customer:', error);
          this.$toast?.error('Fehler beim Löschen des Kunden');
        }
      }
    },

    getCustomerStatusClass(status) {
      return CustomerFormatter.getCustomerStatusClass(status);
    },

    getCustomerStatusText(status) {
      return CustomerFormatter.getCustomerStatusText(status);
    },

    formatDate(dateString) {
      return CustomerFormatter.formatDate(dateString);
    }
  }
}
</script>

<style scoped>
.customer-show-view {
  font-family: 'Inter', sans-serif;
  padding: 1.5rem;
}

.header-section {
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.header-left h2 {
  color: #495057;
  font-weight: 600;
}

.card {
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

.card-title {
  color: #495057;
  font-weight: 600;
}

.card-body {
  padding: 1.5rem;
}

.info-group {
  margin-bottom: 1rem;
}

.info-label {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  display: block;
}

.info-value {
  color: #495057;
  font-size: 0.95rem;
  line-height: 1.4;
}

.contact-link {
  color: #007bff;
  text-decoration: none;
  transition: color 0.15s ease;
}

.contact-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.contact-item {
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.contact-name {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.contact-detail {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.contact-detail i {
  color: #adb5bd;
}

.notes-content {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #007bff;
  white-space: pre-wrap;
  font-size: 0.95rem;
  line-height: 1.5;
}

.orders-count {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.order-link {
  color: #2563eb;
  text-decoration: none;
}

.order-link:hover {
  text-decoration: underline;
}

.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #9ca3af;
}

.empty-state i {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
  color: #6b7280;
}

.loading-container,
.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

/* Status row colouring */
:deep(.row-status-inquiry)    { background-color: rgba(254, 70, 56,  0.12) !important; }
:deep(.row-status-ordered)    { background-color: rgba(255,   0,   0, 0.22) !important; }
:deep(.row-status-prepress)   { background-color: rgba(255, 204, 0,   0.22) !important; }
:deep(.row-status-plates)     { background-color: rgba(255, 255, 153, 0.40) !important; }
:deep(.row-status-printing)   { background-color: rgba(0,   255, 0,   0.16) !important; }
:deep(.row-status-processing) { background-color: rgba(204, 255, 255, 0.55) !important; }
:deep(.row-status-shipping)   { background-color: rgba(112,  48, 160, 0.28) !important; }
:deep(.row-status-delivered)  { background-color: rgba(51,  102, 255, 0.16) !important; }
:deep(.row-status-invoiced)   { background-color: rgba(192, 192, 192, 0.30) !important; }
:deep(.row-status-archived)   { background-color: rgba(224, 224, 224, 0.40) !important; }

/* Badge styles */
.badge {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  text-align: center;
}

.customer-status-active   { background-color: #28a745; color: #fff; }
.customer-status-inactive { background-color: #dc3545; color: #fff; }
.customer-status-pending  { background-color: #ffc107; color: #000; }
.customer-status-default  { background-color: #6c757d; color: #fff; }

@media (max-width: 768px) {
  .customer-show-view { padding: 1rem; }
  .header-section { flex-direction: column; gap: 1rem; }
  .header-right { align-self: stretch; }
  .header-right .btn { width: 100%; margin-bottom: 0.5rem; }
}
</style>
